import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize, filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { 
  DataService, 
  MapServiceProvider, 
  WorkflowContextService 
} from '../../../../services';
import { Client, MapLayer, MapService } from 'src/app/models';
import { MapLayerField } from 'src/app/models/map-layer-field';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';

@Component({
  selector: 'wm-settings-map-services',
  standalone: false,
  templateUrl: './settings-map-services.component.html',
  styleUrls: ['./settings-map-services.component.scss']
})
export class SettingsMapServicesComponent implements OnInit, OnDestroy {
  isInitialLoading = false; 
  loadingLayers: { [key: string]: boolean } = {}; 

  showNewInline = false;
  clientSub$: Subscription;
  routerSub$: Subscription;
  currentClient: Client;
  activeDropdown: string | null = null; 
  activeEditId: string | null = null;

  confirmData: CommonConfirmationModalData = {
    open: false,
    modalDescp: '',
    modalSubDescp: '',
    yesNoBtn: true,
    inTime: 300,
    outTime: 200,
    status: 'default'
  };
  pendingDeleteService: { svc: MapService, index: number } | null = null;

  services: MapService[] = [];
  newService: MapService;
  availableLayers: { [key: string]: MapLayer[] } = {};
  availableFields: { [key: string]: MapLayerField[] } = {};

  originalServicesCache: { [id: string]: MapService } = {};
  
  private scrollPosition = 0;
  private activeScrollContainer: Element | null = null;

  constructor(
    private _dataSvc: DataService,
    private _mapSvc: MapServiceProvider,
    private _context: WorkflowContextService,
    private _toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.resetNewService();
    if (history.state && history.state.services) {
      this.services = history.state.services;
    }

    if (this._context.client) {
      this.currentClient = this._context.client;
      if (this.services.length === 0) {
        this.loadMapServices();
      }
    } else {
      this.clientSub$ = this._context.client$.subscribe(client => {
        this.currentClient = client;
        if (this.services.length === 0) {
          this.loadMapServices();
        }
      });
    }

    this.syncUrl();
    this.routerSub$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.syncUrl();
      this.restoreScrollPosition();
    });
  }

  ngOnDestroy() {
    if (this.clientSub$) {
      this.clientSub$.unsubscribe();
    }
    if (this.routerSub$) {
      this.routerSub$.unsubscribe();
    }
  }

  private saveScrollPosition() {
    this.activeScrollContainer = document.querySelector('.mat-sidenav-content, .mat-drawer-content, .main-content') || document.documentElement;
    this.scrollPosition = this.activeScrollContainer.scrollTop || window.scrollY || 0;
  }

  private restoreScrollPosition() {
    setTimeout(() => {
      if (this.activeScrollContainer && this.activeScrollContainer !== document.documentElement) {
        this.activeScrollContainer.scrollTop = this.scrollPosition;
      } else {
        window.scrollTo({ top: this.scrollPosition, behavior: 'instant' } as any);
      }
    }, 10); 
  }

  syncUrl() {
    const urlStr = this.router.url;
    if (urlStr.includes('/maps/add')) {
      this.setupNewEditor();
    } else if (urlStr.includes('/maps/edit/')) {
      const id = urlStr.split('/maps/edit/')[1].split(/[?#]/)[0];
      this.activeEditId = id;
      this.showNewInline = false;
      this.applyExpand();
    } else {
      this.closeAllEditors();
    }
  }

  loadMapServices() {
    if (!this.currentClient) return;
    this.isInitialLoading = true;
    this._dataSvc.getMapServices(this.currentClient.id)
      .pipe(finalize(() => this.isInitialLoading = false))
      .subscribe(
        (res) => { 
          this.services = res || []; 
          this.applyExpand();
        },
        (err) => { 
          this._toastr.error('Failed to load map services'); 
          console.error(err); 
        }
      );
  }

  applyExpand() {
    if (this.activeEditId && this.services.length > 0) {
      this.services.forEach(s => s['expanded'] = false);
      const index = this.services.findIndex(s => s.id === this.activeEditId);
      if (index > -1) {
        if (!this.originalServicesCache[this.services[index].id]) {
          this.originalServicesCache[this.services[index].id] = JSON.parse(JSON.stringify(this.services[index]));
        }
        this.services[index]['expanded'] = true;
        
        setTimeout(() => {
           const cardEl = document.getElementById('svc-card-' + index);
           if (cardEl) {
               cardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
           }
        }, 50);

        const currentLayers = this.availableLayers[index];
        const needsFetch = !currentLayers || currentLayers.length === 0;

        if (this.services[index].endpointUrl && !this.loadingLayers[index] && needsFetch) {
          this.fetchLayers(this.services[index], index, true);
        }
      }
    }
  }

  navigateToBase() {
    this.saveScrollPosition();
    const baseUrl = this.router.url.split('/maps')[0] + '/maps/list';
    this.router.navigateByUrl(baseUrl, { state: { services: this.services } });
  }

  fetchLayers(mapService: MapService, key: string | number, isSilent = false) {
    if (!mapService.endpointUrl) return;

    this.loadingLayers[key] = true;
    
    this._mapSvc.getMapLayers(mapService.endpointUrl)
      .pipe(finalize(() => {
        this.loadingLayers[key] = false;
      }))
      .subscribe(
        (layers) => {
          this.availableLayers[key] = layers || [];
          
          if (layers && layers.length > 0) {
            if (!mapService.parcelLayer) {
              this.selectLayer(mapService, key, layers[0].name);
            } else {
              this.updateFields(mapService.parcelLayer, key);
            }
            if (!isSilent) {
              this._toastr.info('Map layers loaded');
            }
          } else {
            this._toastr.error('Map Service not found at configured URL. Please confirm and update the URL.');
          }
        },
        (err) => {
          this._toastr.error('Map Service not found at configured URL. Please confirm and update the URL.');
          this.availableLayers[key] = [];
        }
      );
  }

  updateFields(layerName: string, key: string | number) {
    const layers = this.availableLayers[key];
    if (layers) {
      const selectedLayer = layers.find(l => l.name === layerName);
      if (selectedLayer) {
        this.availableFields[key] = selectedLayer.fields;
      } else {
        this.availableFields[key] = [];
      }
    }
  }

  toggleDropdown(id: string) {
    this.activeDropdown = (this.activeDropdown === id) ? null : id;
  }

  closeDropdown() {
    this.activeDropdown = null;
  }

  selectLayer(mapService: MapService, key: string | number, layerName: string) {
    mapService.parcelLayer = layerName;
    mapService.parcelLayerKeyField = ''; 
    
    this.updateFields(layerName, key);

    const fields = this.availableFields[key];
    if (fields && fields.length > 0) {
       mapService.parcelLayerKeyField = fields[0].name;
    }

    this.closeDropdown();
  }

  selectField(mapService: MapService, key: string | number, fieldName: string) {
    mapService.parcelLayerKeyField = fieldName;
    this.closeDropdown();
  }

  isNewServiceActive(): boolean {
    return this.activeDropdown !== null && this.activeDropdown.startsWith('new-');
  }
  
  isServiceActive(index: number): boolean {
    return this.activeDropdown !== null && this.activeDropdown.startsWith(`svc-${index}-`);
  }

  toggleAccordion(index: number) {
    this.saveScrollPosition();
    const svc = this.services[index];
    this.activeDropdown = null;

    if (!svc['expanded']) {
      if (!this.originalServicesCache[svc.id]) {
        this.originalServicesCache[svc.id] = JSON.parse(JSON.stringify(svc));
      }
      const baseUrl = this.router.url.split('/maps')[0] + '/maps';
      this.router.navigateByUrl(`${baseUrl}/edit/${svc.id}`, { state: { services: this.services } });
    } else {
      if (this.originalServicesCache[svc.id]) {
        Object.assign(this.services[index], JSON.parse(JSON.stringify(this.originalServicesCache[svc.id])));
        delete this.originalServicesCache[svc.id];
      }
      this.navigateToBase();
    }
  }

  openNewService() {
    this.saveScrollPosition();
    const baseUrl = this.router.url.split('/maps')[0] + '/maps';
    this.router.navigateByUrl(`${baseUrl}/add`, { state: { services: this.services } });
  }

  setupNewEditor() {
    this.showNewInline = true;
    this.activeEditId = null;
    this.services.forEach(s => s['expanded'] = false);
    if (!this.newService) {
      this.resetNewService();
    }
  }

  closeAllEditors() {
    this.showNewInline = false;
    this.activeEditId = null;
    this.services.forEach(s => s['expanded'] = false);
  }

  closeNewService() {
    this.activeDropdown = null;
    this.navigateToBase();
  }

  resetNewService() {
    this.newService = this._mapSvc.createMapService(); 
    this.newService.name = '';
    this.newService.endpointUrl = '';
    this.newService.mapKeyValue = '';
    this.newService.parcelLayer = '';
    this.newService.parcelLayerKeyField = '';
    
    this.availableLayers['new'] = [];
    this.availableFields['new'] = [];
  }

  saveNewService() {
    if (!this.newService.name || !this.newService.endpointUrl) {
      this._toastr.warning('Name and URL are required');
      return;
    }
    this.isInitialLoading = true; 
    if (this.currentClient) {
      this.newService.client = this.currentClient;
      this.newService.clientID = this.currentClient.id;
    }

    const payload = { ...this.newService };
    delete payload['expanded'];
    if (payload['$id']) delete payload['$id'];

    this._dataSvc.saveMapService(payload)
      .pipe(finalize(() => this.isInitialLoading = false))
      .subscribe(
        (savedSvc) => {
          this._toastr.success('Map Service created');
          this.services.push(savedSvc);
          this.resetNewService();
          this.navigateToBase(); 
        },
        (err) => this._toastr.error('Error saving service')
      );
  }

  saveService(index: number) {
    const svc = this.services[index];
    this.isInitialLoading = true; 

    const payload = { ...svc };
    delete payload['expanded'];
    if (payload['$id']) delete payload['$id'];

    this._dataSvc.saveMapService(payload)
      .pipe(finalize(() => this.isInitialLoading = false))
      .subscribe(
        (res) => {
          this._toastr.success('Saved successfully');
          if (res) {
             res['expanded'] = svc['expanded']; 
             this.services[index] = res; 
          }
          if (this.originalServicesCache[this.services[index].id]) {
            delete this.originalServicesCache[this.services[index].id];
          }
          this.navigateToBase();
        },
        (err) => this._toastr.error('Error updating service')
      );
  }

  requestDelete(svc: MapService, index: number) {
    this.pendingDeleteService = { svc, index };
    this.confirmData = {
      ...this.confirmData,
      open: true,
      modalDescp: `Are you sure you want to delete "${svc.name}"?`,
      modalSubDescp: 'This action cannot be undone.',
      yesNoBtn: true,
      replaceYesWith: 'DELETE',
      replaceNoWith: 'CANCEL',
    };
  }

  confirmDelete() {
    if (!this.pendingDeleteService) {
      this.closeConfirm();
      return;
    }
    const { svc, index } = this.pendingDeleteService;
    this.isInitialLoading = true;

    const payload = { ...svc };
    delete payload['expanded'];
    if (payload['$id']) delete payload['$id'];

    this._mapSvc.deleteMapService(payload)
      .pipe(finalize(() => this.isInitialLoading = false))
      .subscribe(
        () => {
          this._toastr.success('Service removed');
          this.services.splice(index, 1);
          if (this.originalServicesCache[svc.id]) {
            delete this.originalServicesCache[svc.id];
          }
          if (this.activeEditId === svc.id) {
            this.navigateToBase();
          }
        },
        (err) => this._toastr.error('Error deleting service')
      );
    this.pendingDeleteService = null;
    this.closeConfirm();
  }

  cancelDelete() {
    this.pendingDeleteService = null;
    this.closeConfirm();
  }

  private closeConfirm() {
    this.confirmData = { ...this.confirmData, open: false };
  }

  cancelEdit(index: number) {
    this.saveScrollPosition();
    const svc = this.services[index];
    if (this.originalServicesCache[svc.id]) {
      Object.assign(this.services[index], JSON.parse(JSON.stringify(this.originalServicesCache[svc.id])));
      delete this.originalServicesCache[svc.id];
    }
    this.activeDropdown = null;
    this.navigateToBase();
  }

  async reindex(index: number) {
    const svc = this.services[index];
    this.isInitialLoading = true;
    try {
      const isDiscrepancies = await this._dataSvc.indexMapServiceLayers(svc.id).toPromise();
      this.isInitialLoading = false;
      if (isDiscrepancies) {
        this._toastr.warning('Index complete, discrepancies found.');
      } else {
        this._toastr.success('Map service re-indexed successfully.');
      }
    } catch (err) {
      this.isInitialLoading = false;
      this._toastr.error('Failed to re-index map service.');
    }
  }

  hasLayers(key: string | number): boolean {
    return this.availableLayers[key] && this.availableLayers[key].length > 0;
  }
}